import { Fragment, useEffect, useCallback } from "react";
import Papa from "papaparse";
import useAddChargebreakdown from "./hooks/useAddChargebreakdown";
import { IAddChargebreakdownCsvData } from "./interface/AddChargebreakdownInterface";
import { DropZone, Flex, useToast, Button, Card, Icon, Icons, Paragraph, Tooltip, Division } from "@ir/ui";
import { acceptedDateFormats, maxChargebreakdownNumberCharacters } from "./utils/AddChargebreakdownUtils";

// Encodings to try for initial decoding
const DECODE_ENCODINGS = ["windows-1252", "utf-8", "utf8", "iso-8859-1", "ascii"] as const;
type DecodeEncoding = (typeof DECODE_ENCODINGS)[number];

const MAX_FILE_SIZE_IN_KB = 30; // 30 KB
const MAX_FILE_SIZE = MAX_FILE_SIZE_IN_KB * 1024; // 30 KB

const AddChargebreakdownDropzone = () => {
  const { setImportedFileData, importedFileError, setImportedFileError, importedFile, setImportedFile, clearAllData } = useAddChargebreakdown();

  const { error } = useToast();

  const unsetImportedFile = useCallback(() => {
    clearAllData();
  }, [clearAllData]);

  const displayErrorMessage = useCallback((message: string) => {
    error({ content: message });
  }, [error]);

  /**
   * Convert file size to KB or MB
   * @param size - file size in bytes
   * @returns file size in KB or MB
   */
  const fileSizeUnit = (size: number) => {
    if (size < 1024) {
      return size + " Bytes";
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(2) + " KB";
    } else {
      return (size / 1024 / 1024).toFixed(2) + " MB";
    }
  };

  const handleOnDrop = async (files: File[]) => {
    if (files.length > 0) {
      for (const file of files) {
        const fileExtension = file.name.split(".").pop();
        if (file.type.toUpperCase() === "TEXT/CSV" || fileExtension?.toUpperCase() === "CSV") {
          unsetImportedFile();

          // Get file size
          setImportedFile({ file });
          const fileSize = file.size;
          if (fileSize > MAX_FILE_SIZE) {
            setImportedFileError({ message: "File size cannot exceed " + MAX_FILE_SIZE_IN_KB + " KB." });
            return;
          }
          setImportedFileError(null);
          return;
        }
      }
      displayErrorMessage("Invalid file type.");
    }
  };

  const doDataFilter = useCallback((data: IAddChargebreakdownCsvData[]) => {
    return data.filter(item => {
      return !Object.values(item).every(value => value === null || value === "");
    });
  }, []);

  useEffect(() => {
    if (importedFile) {
      const processFile = async () => {
        try {
          const csvRawText = await importedFile.file.arrayBuffer();

          // Decode the CSV file
          let csvText: string | null = null;
          let usedDecodeEncoding: DecodeEncoding | null = null;

          // Try to decode the CSV file with the supported encodings
          for (const encoding of DECODE_ENCODINGS) {
            try {
              const decoder = new TextDecoder(encoding, { fatal: true });
              const decodedText = decoder.decode(csvRawText);
              csvText = decodedText;
              usedDecodeEncoding = encoding;
              break;
            } catch {
              continue;
            }
          }

          usedDecodeEncoding = usedDecodeEncoding ?? "utf-8";

          // Display error message
          if (!csvText || csvText.trim() === "") {
            displayErrorMessage(csvText?.trim() === "" ? "CSV file is blank." : "Failed to decode CSV with any supported encoding.");
            return;
          }

          // Parse the CSV file
          Papa.parse(csvText, {
            encoding: usedDecodeEncoding,
            header: true,
            skipEmptyLines: true,
            transformHeader: header => {
              // Check Header text has first letter _ underscore and 2nd letter is number and length is less than 3 then return empty string
              if (header.startsWith("_") && !Number.isNaN(Number(header[1])) && header.length <= 3) {
                return "";
              }
              return header.trim();
            },
            transform: value => {
              return value.trim();
            },
            dynamicTyping: true,
            complete: (results: Papa.ParseResult<IAddChargebreakdownCsvData>) => {
              const _results = doDataFilter(results.data) ?? [];

              if (!results.meta.fields || results.meta.fields.length <= 1) {
                setImportedFileError({ message: "Invalid CSV file." });
                return;
              } else if (_results.length === 0) {
                setImportedFileError({ message: "No data found in CSV file." });
                return;
              }

              // Set the headers and data
              setImportedFileData({ headers: results.meta.fields, data: _results });
            },
            error: () => {
              setImportedFileError({ message: "Failed to parse CSV file." });
            },
          });
        } catch {
          setImportedFileError({ message: "Failed to read file." });
        }
      };

      processFile();
    }
  }, [importedFile, displayErrorMessage, setImportedFileError, setImportedFileData, doDataFilter]);

  return (
    <Fragment>
      <DropZone
        onDrop={handleOnDrop}
        description={
          <Flex direction="column" align="center">
            <Division className="p-3 text-left" style={{ maxWidth: "700px", fontWeight: "500", color: "#3F3F46" }}>
              <ul style={{ listStyle: "disc", listStylePosition: "inside" }}>
                <li>
                  <b>File size </b> must be <b>less than {MAX_FILE_SIZE_IN_KB} KB.</b>
                </li>
                <li>
                  <b>Date</b> must be in <b>{acceptedDateFormats.join(" or ")}</b> format.
                </li>
                <li>
                  <b>Amount</b> must be <b>numeric only</b> – no special characters, symbols, or commas.
                </li>
                <li>Each cell value in the CSV must not exceed {maxChargebreakdownNumberCharacters} characters.</li>
              </ul>
            </Division>
          </Flex>
        }
        selectionMode="single"
        allowedFileTypesOnSelect={["text/csv"]}
      />

      {importedFile && (
        <Card className="p-3 border-1 border-color-grey-50 mt-2">
          <Flex direction="row" align="center" justifyContent="space-between">
            <Flex align="start" gap={0.5}>
              <Flex>
                <Icon className="text-grey-300" name={Icons.File} color={"grey"} />
              </Flex>
              <Flex className="flex-1" direction="column">
                <Paragraph className="m-0 font-medium text-grey-700 text-md">{importedFile?.file.name}</Paragraph>
                <Paragraph className="m-0 font-normal text-grey-400 text-md">{fileSizeUnit(importedFile?.file.size ?? 0)}</Paragraph>
                {importedFileError && <Paragraph className="m-0 font-normal text-error-500 m-0 ">{importedFileError.message}</Paragraph>}
              </Flex>
            </Flex>
            <Flex align="center" justifyContent="center">
              <Flex align="center" justifyContent="center">
                {importedFileError ? null : <Icon name={Icons.CheckCircle} color="grey" className="text-success-500 mx-2" />}
                <Tooltip content={"Remove file"}>
                  <Button variant="clear" noStyle color="grey" onPress={unsetImportedFile}>
                    <Icon name={Icons.Close} color="grey" />
                  </Button>
                </Tooltip>
              </Flex>
            </Flex>
          </Flex>
        </Card>
      )}
    </Fragment>
  );
};

export default AddChargebreakdownDropzone;
